import { Accordion, Badge, Card, createStyles, Group, List, Text, Title } from '@mantine/core'
import React from 'react'

import { PictureCard } from '@/components/surfaces'
import { hasDistinctChannel } from '@/lib/utils'
import {
  Channel as ChannelType,
  Institution as InstitutionType,
  ProductCategory as ProductCategoryType
} from '@/types'

const useStyles = createStyles(theme => ({
  item: {
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    '&:last-child': { borderBottom: 'none' }
  },
  itemWrapper: { width: '100%' }
}))

interface Props {
  description?: string
  categories?: ProductCategoryType[]
  brand?: string
  upc?: string
  weight?: string
  requiresShipping?: boolean
  channel?: ChannelType
  institution?: InstitutionType | null
}

export default function Informations({
  description,
  categories,
  brand,
  upc,
  weight,
  requiresShipping,
  channel,
  institution
}: Props) {
  const { classes } = useStyles()

  // Constants
  const items = []

  if (brand) items.push({ key: 'brand', title: 'Marca', value: brand })
  if (upc) items.push({ key: 'upc', title: 'Código identificação', value: upc })
  if (requiresShipping) items.push({ key: 'weight', title: 'Peso', value: weight })

  return (
    <>
      <Card.Section>
        <Title order={3} p="md">
          Informações
        </Title>
        {!!description && (
          <Accordion defaultValue="description">
            <Accordion.Item value="description">
              <Accordion.Control>
                <Title order={4}>Descrição</Title>
              </Accordion.Control>
              <Accordion.Panel>
                <Text dangerouslySetInnerHTML={{ __html: description }} />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}
        <List
          listStyleType="none"
          classNames={{ item: classes.item, itemWrapper: classes.itemWrapper }}>
          {!!channel?.title && (
            <List.Item>
              <Group position="apart" p="md" noWrap>
                <Text>
                  <strong>Canal</strong>
                </Text>
                <PictureCard
                  isReverted
                  disableTooltip
                  title={channel.title}
                  picture={channel.logo || null}
                />
              </Group>
            </List.Item>
          )}
          {!!institution && hasDistinctChannel(channel, institution) && (
            <List.Item>
              <Group position="apart" p="md" noWrap>
                <Text>
                  <strong>Instituição</strong>
                </Text>
                <PictureCard
                  isReverted
                  disableTooltip
                  title={institution.displayName}
                  picture={institution.logo || null}
                />
              </Group>
            </List.Item>
          )}
          {Array.isArray(categories) && !!categories.length && (
            <List.Item>
              <Group position="apart" p="md">
                <Text>
                  <strong>Categorias</strong>
                </Text>
                <Group spacing="xs">
                  {categories.map((category: ProductCategoryType) => (
                    <Badge title={category.name} key={category.slug}>
                      {category.fullName}
                    </Badge>
                  ))}
                </Group>
              </Group>
            </List.Item>
          )}
          {Array.isArray(items) &&
            items.map(item => (
              <List.Item key={item.key}>
                <Group position="apart" p="md">
                  <Text>
                    <strong>{item.title}</strong>
                  </Text>
                  <Text>{item.value}</Text>
                </Group>
              </List.Item>
            ))}
        </List>
      </Card.Section>
    </>
  )
}
